import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, DatePicker, Empty, Modal, Space, Table, Tabs, Tag, Tooltip, message } from "antd";
import {
  CheckOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  SettingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Dayjs } from "dayjs";
import ProjectPageShell, { useProjectContext } from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import {
  DailyApprovalRow,
  DailyApprovalTab,
  dailyApprovalApis,
  normaliseRow,
} from "../../../api/DailyApprovals";
import { ApprovalStatusPill, fmtDay, fmtWhen } from "./approvalUi";
import ApprovalSetupDrawer from "./ApprovalSetupDrawer";
import "../execution/execution.css";
import "./dailyApprovals.css";

// Daily Approvals — the queue of site days waiting for review. One row per
// project-day; the review itself happens on its own page.
const PAGE_SIZE = 25;

const DailyApprovalsPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const context = useProjectContext();
  const projectId = params.projectId || context.projectId;
  const projectName = context.projectName;

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))));
  }, [dispatch, location]);

  const [tab, setTab] = useState<DailyApprovalTab>("MINE");
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [rows, setRows] = useState<DailyApprovalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ mine: 0, pending: 0 });
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    dailyApprovalApis
      .list({
        tab,
        projectId,
        fromDate: range?.[0]?.format("YYYY-MM-DD"),
        toDate: range?.[1]?.format("YYYY-MM-DD"),
        pageNo,
        pageSize: PAGE_SIZE,
      })
      .then((res) => {
        if (res?.status !== true) {
          message.error(res?.message || "Could not load the approval queue");
          return;
        }
        setRows((res.data?.rows || []).map(normaliseRow));
        setTotal(Number(res.data?.total) || 0);
        setCounts({ mine: Number(res.data?.mineCount) || 0, pending: Number(res.data?.pendingCount) || 0 });
        setSelected([]);
      })
      .finally(() => setLoading(false));
  }, [projectId, tab, range, pageNo]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = (row: DailyApprovalRow) =>
    navigate(`/pms/project/daily-approvals/review/${row.projectId}/${row.reportDate}`, { state: location.state });

  const oldestWaiting = useMemo(
    () => (tab === "MINE" || tab === "PENDING" ? Math.max(0, ...rows.map((row) => row.daysWaiting)) : 0),
    [rows, tab]
  );

  const bulkApprove = () => {
    const items = rows
      .filter((row) => selected.includes(row.dprId))
      .map((row) => ({ dprId: row.dprId, round: row.round, level: row.currentLevel }));
    Modal.confirm({
      title: `Approve ${items.length} day${items.length === 1 ? "" : "s"}?`,
      content: "Only days with no flags can be approved together. Each is approved at its current level.",
      okText: "Approve",
      onOk: async () => {
        setBulkBusy(true);
        try {
          const res = await dailyApprovalApis.bulkApprove(items);
          if (res?.status !== true) {
            message.error(res?.message || "Could not approve");
            return;
          }
          const failed = (res.data?.results || []).filter((r: any) => !r.ok);
          if (failed.length) message.warning(`${res.data?.message}. ${failed[0].message}`, 6);
          else message.success(res.data?.message || "Approved");
          load();
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "reportDate",
      width: 130,
      render: (value: string, row: DailyApprovalRow) => (
        <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => openReview(row)}>
          {fmtDay(value)}
        </Button>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 190,
      render: (_: any, row: DailyApprovalRow) => (
        <Space size={4} direction="vertical">
          <ApprovalStatusPill state={row} />
          {row.round > 1 && <span className="pms-appr-sub">Round {row.round}</span>}
        </Space>
      ),
    },
    {
      title: "Submitted by",
      key: "submitted",
      width: 190,
      render: (_: any, row: DailyApprovalRow) => (
        <span>
          {row.submittedByName || "—"}
          <div className="pms-appr-sub">{fmtWhen(row.submittedAt)}</div>
        </span>
      ),
    },
    {
      title: "Waiting",
      dataIndex: "daysWaiting",
      width: 90,
      align: "right" as const,
      render: (value: number, row: DailyApprovalRow) =>
        row.status === "Submitted" || row.status === "InReview" ? (
          <span className="pms-appr-num" style={{ color: value >= 3 ? "#b23131" : undefined }}>
            {value === 0 ? "Today" : `${value} d`}
          </span>
        ) : (
          <span className="pms-appr-sub">—</span>
        ),
    },
    { title: "Faces", dataIndex: "faceCount", width: 70, align: "right" as const, render: (v: number) => <span className="pms-appr-num">{v}</span> },
    { title: "BOQ lines", dataIndex: "boqLineCount", width: 90, align: "right" as const, render: (v: number) => <span className="pms-appr-num">{v}</span> },
    { title: "Material", dataIndex: "materialTaskDayCount", width: 90, align: "right" as const, render: (v: number) => <span className="pms-appr-num">{v}</span> },
    { title: "Machines", dataIndex: "machineCount", width: 90, align: "right" as const, render: (v: number) => <span className="pms-appr-num">{v}</span> },
    {
      title: "Flags",
      dataIndex: "flagCount",
      width: 80,
      align: "right" as const,
      render: (value: number) =>
        value ? <Tag color="gold" icon={<WarningOutlined />}>{value}</Tag> : <Tag color="green">0</Tag>,
    },
    {
      title: "Last action",
      key: "last",
      render: (_: any, row: DailyApprovalRow) => (
        <span>
          {row.lastActionByName || "—"}
          <div className="pms-appr-sub" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.lastRemarks || fmtWhen(row.lastActionAt)}
          </div>
        </span>
      ),
    },
    {
      title: "",
      key: "open",
      width: 100,
      render: (_: any, row: DailyApprovalRow) => (
        <Button size="small" type={row.canAct ? "primary" : "default"} onClick={() => openReview(row)}>
          {row.canAct ? "Review" : "Open"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <ProjectPageShell
        headTitle="Daily Approvals"
        breadcrumb={[{ label: projectName, onClick: () => navigate(-1) }, { label: "Daily Approvals" }]}
        eyebrow="Execution & Actuals"
        title="Daily Approvals"
        onBack={() => navigate(-1)}
        actions={
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setSetupOpen(true)}>
              Approval setup
            </Button>
            {tab === "MINE" && (
              <Tooltip title={selected.length ? "" : "Select days with no flags"}>
                <Button type="primary" icon={<CheckOutlined />} disabled={!selected.length} loading={bulkBusy} onClick={bulkApprove}>
                  Approve selected ({selected.length})
                </Button>
              </Tooltip>
            )}
          </Space>
        }
        stats={
          <>
            <StatCard label="Waiting for me" value={counts.mine} icon={<InboxOutlined />} onClick={() => setTab("MINE")} active={tab === "MINE"} />
            <StatCard label="All pending" value={counts.pending} icon={<ClockCircleOutlined />} onClick={() => setTab("PENDING")} active={tab === "PENDING"} />
            <StatCard
              label="Oldest waiting on this page"
              value={tab === "MINE" || tab === "PENDING" ? (rows.length ? `${oldestWaiting} day${oldestWaiting === 1 ? "" : "s"}` : "—") : "—"}
              icon={<WarningOutlined />}
              tone={oldestWaiting >= 3 ? "onhold" : "brand"}
            />
          </>
        }
        tabs={
          <Tabs
            className="pms-tabs"
            activeKey={tab}
            onChange={(key) => {
              setPageNo(1);
              setTab(key as DailyApprovalTab);
            }}
            items={[
              { key: "MINE", label: `Waiting for me (${counts.mine})` },
              { key: "PENDING", label: `All pending (${counts.pending})` },
              { key: "APPROVED", label: "Approved" },
              { key: "REJECTED", label: "Rejected" },
            ]}
          />
        }
        toolbar={
          <div className="pms-exec-bar">
            <DatePicker.RangePicker
              value={range as any}
              onChange={(value) => {
                setPageNo(1);
                setRange(value as any);
              }}
              format="DD-MMM-YYYY"
            />
            <Button icon={<ReloadOutlined />} onClick={load} />
          </div>
        }
      >
        <Table
          size="small"
          rowKey="dprId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1300 }}
          rowSelection={
            tab === "MINE"
              ? {
                  selectedRowKeys: selected,
                  onChange: setSelected,
                  getCheckboxProps: (row: DailyApprovalRow) => ({ disabled: !row.canAct || row.flagCount > 0 }),
                }
              : undefined
          }
          onRow={(row) => ({ onDoubleClick: () => openReview(row) })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={tab === "MINE" ? "Nothing is waiting for you" : "No days here"}
              />
            ),
          }}
          pagination={{ current: pageNo, pageSize: PAGE_SIZE, total, onChange: setPageNo, showSizeChanger: false }}
        />
      </ProjectPageShell>
      <ApprovalSetupDrawer open={setupOpen} projectId={projectId} onClose={() => setSetupOpen(false)} onSaved={load} />
    </>
  );
};

export default DailyApprovalsPage;
